import { useInfiniteQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useRef, useEffect, useMemo } from 'react';
import { useNavigate, useNavigationType, useParams } from 'react-router';

import { useLivescoresOptions } from '../store';
import { loadTabsByDate } from '../store/actions/tabs';
import type { LivescoreTab } from '../store/types';
import { scrollToTab } from '../utils/scroll';

import { useFilters } from './filters';

import { uniqBy } from '@utilities/collection-utils';

history.scrollRestoration = 'manual';

dayjs.extend(isSameOrAfter);

export const useLivescoresTabs = () => {
  const { options } = useLivescoresOptions();
  const { filters } = useFilters();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  const params = useParams();
  const type = useNavigationType();

  const lastTabId = useRef(params.tabId);

  const setSelectedTab = (tab: LivescoreTab, replace = false) => {
    if (lastTabId.current === tab.id) return;

    navigateRef.current(`/${tab.id}`, { replace: replace });
  };

  const initialPageParam = useMemo(
    () => ({ startDate: dayjs(lastTabId.current).startOf('month'), endDate: dayjs(lastTabId.current).endOf('month') }),
    [],
  );

  const {
    data: tabs,
    isLoading,
    fetchPreviousPage,
    isFetchingPreviousPage,
    hasPreviousPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['livescores-calendar-tabs', options, filters],
    queryFn: ({ pageParam, signal }) => {
      const { startDate, endDate } = pageParam ?? {};

      return loadTabsByDate(
        {
          startDate,
          endDate,
          competitionIds: filters.competition ? [filters.competition.id] : options.competitionIds,
          phase: filters.competition?.phase,
        },
        signal,
      );
    },
    initialPageParam,
    getPreviousPageParam: (firstPage, _allPages, firstPageParam) =>
      firstPage.length
        ? {
            startDate: dayjs(firstPageParam.startDate).subtract(1, 'month'),
            endDate: dayjs(firstPageParam.startDate).subtract(1, 'day'),
          }
        : null,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.length
        ? {
            startDate: dayjs(lastPageParam.endDate).add(1, 'day'),
            endDate: dayjs(lastPageParam.endDate).add(1, 'month'),
          }
        : undefined,
    select: (data) => uniqBy(data?.pages?.flatMap((p) => p) ?? [], 'id') ?? [],
  });

  const selectedTab = useMemo(() => tabs?.find((tab) => params.tabId && tab.id === params.tabId), [params.tabId, tabs]);
  const selectedTabIndex = useMemo(() => tabs?.indexOf(selectedTab), [selectedTab, tabs]);

  const defaultTab = useMemo(() => {
    if (!tabs) return null;

    const activeTab = tabs.find((tab) => lastTabId.current && tab.id === lastTabId.current);
    if (activeTab) return activeTab;

    const dateDistance = (a: LivescoreTab, b: LivescoreTab) =>
      Math.abs(dayjs().diff(a.date, 'day')) - Math.abs(dayjs().diff(b.date, 'day'));
    const tabsCopy = tabs.toSorted(dateDistance);
    return tabsCopy.find((tab) => !tab.draw) ?? tabsCopy.at(0);
  }, [tabs]);

  useEffect(() => {
    if (!defaultTab || defaultTab.id === lastTabId.current) return;

    setSelectedTab(defaultTab, true);
  }, [defaultTab]);

  useEffect(() => {
    if (!selectedTab || type !== 'POP') return;

    scrollToTab(selectedTab.id);
  }, [selectedTab, type]);

  return {
    tabs: tabs ?? [],
    loadPrevious: fetchPreviousPage,
    loadNext: fetchNextPage,
    selectedTabIndex,
    selectedTab,
    setSelectedTab,
    isLoading: isLoading,
    isFetchingPreviousPage,
    hasPreviousPage,
  };
};
